import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Inbox, ArrowRight, Loader2, ShieldCheck,
  CheckCircle2, XCircle, Clock, Repeat, Wallet, ArrowDown, ArrowUp, Gift, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { MarketplaceDisclaimer } from '../components/brand/MarketplaceDisclaimer';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DashboardShell } from '../components/brand/DashboardShell';
import { clinicianNavItems, patientNavItems } from '../components/brand/navItems';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  in_home_care: 'In-home care',
  telehealth_visit: 'Telehealth visit',
  wellness_visit: 'Wellness visit',
  companion_care: 'Companion care',
  post_discharge: 'Post-discharge',
  chronic_care: 'Chronic care',
  hospice_palliative: 'Hospice & palliative',
  mobile_urgent_care: 'Mobile urgent care',
  behavioral_health: 'Behavioral health',
  skilled_nursing: 'Skilled nursing',
};

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-50', text: 'text-amber-800', ring: 'ring-amber-200', icon: Clock, label: 'Pending' },
  countered: { bg: 'bg-cn-terracotta-50', text: 'text-cn-terracotta-700', ring: 'ring-cn-terracotta-200', icon: Repeat, label: 'Counter-offered' },
  accepted: { bg: 'bg-cn-sage-100', text: 'text-cn-primary', ring: 'ring-cn-primary/20', icon: CheckCircle2, label: 'Accepted — awaiting payment' },
  declined: { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200', icon: XCircle, label: 'Declined' },
  expired: { bg: 'bg-cn-bone-200', text: 'text-cn-text-muted', ring: 'ring-cn-border', icon: Clock, label: 'Expired' },
  paid: { bg: 'bg-emerald-50', text: 'text-emerald-800', ring: 'ring-emerald-200', icon: Wallet, label: 'Funds secured' },
};

export default function OffersPage() {
  const { user, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [offers, setOffers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counterOpen, setCounterOpen] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(null);
  const [benefitApps, setBenefitApps] = useState([]);
  const [selectedBenefit, setSelectedBenefit] = useState({}); // offerId -> applicationId | 'none'

  const isPatient = user?.role === 'patient';

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      // Benefits applications are patient-only on the backend (403 for
      // clinician / agency / admin). Skip the call unless the caller is
      // a patient to avoid noisy 403s in the network tab.
      const [{ data: offersData }, { data: bookingsData }, benefitsRes] = await Promise.all([
        axios.get(`${API}/offers`, getAuthHeader()),
        axios.get(`${API}/bookings`, getAuthHeader()).catch(() => ({ data: [] })),
        isPatient
          ? axios.get(`${API}/benefits/applications`, getAuthHeader()).catch(() => ({ data: { applications: [] } }))
          : Promise.resolve({ data: { applications: [] } }),
      ]);
      setOffers(offersData || []);
      setBookings(bookingsData || []);
      setBenefitApps(benefitsRes.data?.applications || []);
    } catch (e) {
      toast.error('Could not load offers');
    } finally {
      setLoading(false);
    }
    // Use `isPatient` (primitive) not `user` (object — may not be
    // reference-stable across AuthContext re-renders and would trigger
    // an infinite re-fetch loop via useEffect below).
  }, [getAuthHeader, isPatient]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const counter = async (offerId, newRate, notes) => {
    try {
      await axios.post(
        `${API}/offers/${offerId}/counter`,
        { proposed_rate: Number(newRate), notes: notes || null },
        getAuthHeader()
      );
      toast.success('Counter sent');
      setCounterOpen(null);
      fetchOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not counter');
    }
  };

  const accept = async (offerId) => {
    try {
      await axios.post(`${API}/offers/${offerId}/accept`, {}, getAuthHeader());
      toast.success('Offer accepted');
      fetchOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not accept');
    }
  };

  const decline = async (offerId) => {
    if (!window.confirm('Decline this offer?')) return;
    try {
      await axios.post(`${API}/offers/${offerId}/decline`, {}, getAuthHeader());
      toast.success('Offer declined');
      fetchOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not decline');
    }
  };

  const startCheckout = async (offer, benefitApplicationId = null) => {
    try {
      const body = { offer_id: offer.id, origin_url: window.location.origin };
      if (benefitApplicationId && benefitApplicationId !== 'none') body.benefit_application_id = benefitApplicationId;
      const { data } = await axios.post(`${API}/payments/checkout`, body, getAuthHeader());
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start payment');
    }
  };

  // Benefit credit math mirrors the backend (credit capped at 80% of the visit total)
  const benefitFor = (offer) => {
    if (!benefitApps.length) return null;
    const gross = Number(offer.total_amount) || 0;
    const cap = +(gross * 0.8).toFixed(2);
    const chosenId = selectedBenefit[offer.id];
    if (chosenId === 'none') return { credit: 0, net: gross, app: null };
    const priced = benefitApps.map((a) => ({ app: a, credit: +Math.min(cap, Number(a.est_monthly_value) || cap).toFixed(2) }));
    const chosen = chosenId ? priced.find((p) => p.app.id === chosenId) : priced.sort((x, y) => y.credit - x.credit)[0];
    if (!chosen) return null;
    const net = +(gross - chosen.credit).toFixed(2);
    if (net < 0.5) return { credit: 0, net: gross, app: null };
    return { credit: chosen.credit, net, app: chosen.app };
  };

  const completeVisit = async (bookingId) => {
    if (!window.confirm('Mark this visit as complete? Settlement will release to you.')) return;
    try {
      await axios.post(`${API}/bookings/${bookingId}/complete`, {}, getAuthHeader());
      toast.success('Visit completed — settlement released');
      fetchOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete visit');
    }
  };

  const submitReview = async (bookingId, rating, comment) => {
    try {
      await axios.post(
        `${API}/reviews/booking`,
        { booking_id: bookingId, rating, comment },
        getAuthHeader()
      );
      toast.success('Thank you for your review');
      setReviewOpen(null);
      fetchOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit review');
    }
  };

  const bookingForOffer = (offerId) => bookings.find((b) => b.offer_id === offerId);

  const isClinician = user?.role === 'clinician';

  const navItems = isClinician
    ? clinicianNavItems({ pathname: location.pathname })
    : isPatient
      ? patientNavItems({ pathname: location.pathname })
      : [];

  return (
    <DashboardShell
      navItems={navItems}
      title={isPatient ? 'Offers & negotiations' : 'Incoming offers'}
      subtitle={
        isPatient
          ? 'Negotiate care, transparently. Every offer is secured by verified-settlement.'
          : 'Choose the visits that fit your practice. Accept, counter, or decline.'
      }
      testId="offers-page"
    >
      <main>
        {isPatient && (
          <div className="mb-8 flex justify-end">
            <button
              onClick={() => navigate('/patient/find-clinicians')}
              className="cn-btn cn-btn-primary text-sm"
              data-testid="offers-find-clinicians"
            >
              Find clinicians <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-8 h-8 text-cn-primary animate-spin" />
          </div>
        ) : offers.length === 0 ? (
          <div className="cn-card p-12 text-center" data-testid="offers-empty-state">
            <div className="w-14 h-14 rounded-full bg-cn-sage-100 flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-6 h-6 text-cn-primary" />
            </div>
            <h3 className="font-display text-xl font-semibold mb-2">No offers yet</h3>
            <p className="text-cn-text-muted text-sm max-w-md mx-auto">
              {isPatient
                ? 'When you send an offer to a clinician, it will appear here.'
                : "When families send you offers, they'll appear here for you to accept, counter, or decline."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {offers.map((o) => {
              const s = STATUS_STYLES[o.status] || STATUS_STYLES.pending;
              const Icon = s.icon;
              const otherParty = isPatient ? o.clinician_name : o.patient_name;
              const myTurn = o.last_action_by !== user?.id && ['pending', 'countered'].includes(o.status);
              return (
                <article key={o.id} className="cn-card overflow-hidden" data-testid={`offer-card-${o.id}`}>
                  <div className="p-5 border-b border-cn-border">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-cn-text-muted font-semibold">
                          {isPatient ? 'To clinician' : 'From family'}
                        </div>
                        <div className="font-display font-semibold text-lg mt-0.5">{otherParty}</div>
                        <div className="text-sm text-cn-text-muted">{SERVICE_LABELS[o.service_type] || o.service_type}</div>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 ${s.bg} ${s.text} ring-1 ${s.ring} px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0`}>
                        <Icon className="w-3.5 h-3.5" />
                        {s.label}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-cn-text-muted">Rate</div>
                        <div className="font-display font-bold text-lg">${o.current_rate}/hr</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-cn-text-muted">Duration</div>
                        <div className="font-display font-bold text-lg">{o.duration_hours}h</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-cn-text-muted">Total</div>
                        <div className="font-display font-bold text-lg text-cn-primary">${o.total_amount}</div>
                      </div>
                    </div>
                  </div>

                  {/* History timeline */}
                  <div className="p-5 bg-cn-bone-100/50 border-b border-cn-border">
                    <div className="text-xs uppercase tracking-wider text-cn-text-muted font-semibold mb-2">Negotiation history</div>
                    <ul className="space-y-2">
                      {(o.history || []).slice(-4).map((h, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-xs">
                          {h.actor_role === 'patient' ? (
                            <ArrowUp className="w-3 h-3 text-cn-secondary" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-cn-terracotta-500" />
                          )}
                          <span className="font-medium capitalize text-cn-text">{h.actor_role}</span>
                          <span className="text-cn-text-muted">{h.action}</span>
                          <span className="font-semibold text-cn-primary">${h.rate}/hr</span>
                          {h.notes && <span className="text-cn-text-muted truncate">— {h.notes}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="p-5 flex flex-wrap gap-2">
                    {o.status === 'accepted' && isPatient && (() => {
                      const ben = benefitFor(o);
                      const hasCredit = ben && ben.credit > 0;
                      const payAmount = hasCredit ? ben.net : Number(o.total_amount);
                      return (
                        <div className="w-full space-y-3" data-testid={`offer-checkout-${o.id}`}>
                          {benefitApps.length > 0 ? (
                            <div className="rounded-2xl border border-cn-primary/20 bg-cn-sage-50/60 p-3" data-testid={`benefit-credit-${o.id}`}>
                              <div className="flex items-center justify-between gap-2">
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-cn-primary uppercase tracking-wide">
                                  <Gift className="w-3.5 h-3.5" /> Benefit applied
                                </span>
                                {benefitApps.length > 1 && (
                                  <select
                                    value={selectedBenefit[o.id] || (ben?.app?.id ?? '')}
                                    onChange={(e) => setSelectedBenefit((s) => ({ ...s, [o.id]: e.target.value }))}
                                    className="text-xs rounded-lg border border-cn-border bg-white px-2 py-1 text-cn-text"
                                    data-testid={`benefit-select-${o.id}`}
                                  >
                                    {benefitApps.map((a) => <option key={a.id} value={a.id}>{a.program_name}</option>)}
                                    <option value="none">No benefit</option>
                                  </select>
                                )}
                              </div>
                              <div className="mt-2 space-y-1 text-sm">
                                <div className="flex justify-between text-cn-text-muted">
                                  <span>Visit total</span><span>${Number(o.total_amount).toFixed(2)}</span>
                                </div>
                                {hasCredit && (
                                  <div className="flex justify-between text-cn-primary font-medium" data-testid={`benefit-credit-line-${o.id}`}>
                                    <span>{ben.app?.program_name} credit</span><span>−${ben.credit.toFixed(2)}</span>
                                  </div>
                                )}
                                <div className="flex justify-between font-display font-bold text-cn-text border-t border-cn-border pt-1">
                                  <span>You pay</span>
                                  <span className="text-cn-primary" data-testid={`benefit-net-${o.id}`}>${payAmount.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => navigate('/patient/benefits')}
                              className="w-full text-left rounded-2xl border border-dashed border-cn-primary/40 bg-cn-sage-50/40 p-3 text-xs text-cn-primary inline-flex items-center gap-1.5 hover:bg-cn-sage-50 transition-colors"
                              data-testid={`benefit-nudge-${o.id}`}
                            >
                              <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                              You may qualify for benefits (VA, Medicaid, grants) that lower this cost — check eligibility →
                            </button>
                          )}
                          <button
                            onClick={() => startCheckout(o, hasCredit ? ben.app?.id : null)}
                            className="cn-btn cn-btn-primary text-sm w-full"
                            data-testid={`offer-pay-${o.id}`}
                          >
                            Pay ${payAmount.toFixed(2)} — Secured by Stripe Connect
                            <ShieldCheck className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })()}
                    {o.status === 'paid' && (() => {
                      const b = bookingForOffer(o.id);
                      if (!b) {
                        return (
                          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-semibold">
                            <ShieldCheck className="w-4 h-4" /> Funds secured — awaiting service verification
                          </span>
                        );
                      }
                      if (b.status === 'escrow_held') {
                        if (isClinician) {
                          return (
                            <button
                              onClick={() => completeVisit(b.id)}
                              className="cn-btn cn-btn-accent text-sm"
                              data-testid={`offer-complete-${o.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4" /> Mark visit complete & settle ${b.amount}
                            </button>
                          );
                        }
                        return (
                          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-semibold">
                            <ShieldCheck className="w-4 h-4" /> Funds secured — awaiting clinician to verify visit
                          </span>
                        );
                      }
                      if (b.status === 'released' || b.status === 'completed') {
                        if (isPatient) {
                          return (
                            <button
                              onClick={() => setReviewOpen(b)}
                              className="cn-btn cn-btn-primary text-sm"
                              data-testid={`offer-review-${o.id}`}
                            >
                              Leave a review
                            </button>
                          );
                        }
                        return (
                          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-semibold">
                            <Wallet className="w-4 h-4" /> Visit complete · funds released
                          </span>
                        );
                      }
                      return null;
                    })()}
                    {['pending', 'countered'].includes(o.status) && myTurn && (
                      <>
                        <button
                          onClick={() => accept(o.id)}
                          className="cn-btn cn-btn-primary text-sm"
                          data-testid={`offer-accept-${o.id}`}
                        >
                          Accept ${o.current_rate}/hr
                        </button>
                        <button
                          onClick={() => setCounterOpen(o)}
                          className="cn-btn cn-btn-ghost text-sm"
                          data-testid={`offer-counter-${o.id}`}
                        >
                          <Repeat className="w-4 h-4" /> Counter
                        </button>
                        <button
                          onClick={() => decline(o.id)}
                          className="cn-btn cn-btn-ghost text-sm text-red-700 border-red-200 hover:bg-red-50"
                          data-testid={`offer-decline-${o.id}`}
                        >
                          Decline
                        </button>
                      </>
                    )}
                    {['pending', 'countered'].includes(o.status) && !myTurn && (
                      <div className="text-xs text-cn-text-muted inline-flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> Awaiting {isPatient ? 'clinician' : 'family'} response
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-10">
          <MarketplaceDisclaimer variant="banner" />
        </div>
      </main>

      {/* Counter modal */}
      {counterOpen && (
        <CounterDialog
          offer={counterOpen}
          onClose={() => setCounterOpen(null)}
          onSubmit={(rate, notes) => counter(counterOpen.id, rate, notes)}
        />
      )}
      {/* Review modal */}
      {reviewOpen && (
        <ReviewDialog
          booking={reviewOpen}
          onClose={() => setReviewOpen(null)}
          onSubmit={(rating, comment) => submitReview(reviewOpen.id, rating, comment)}
        />
      )}
    </DashboardShell>
  );
}

function CounterDialog({ offer, onClose, onSubmit }) {
  const [rate, setRate] = useState(offer.current_rate);
  const [notes, setNotes] = useState('');

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="counter-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Counter the offer</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">New rate ($/hr)</Label>
            <Input
              type="number" min={1} step={1}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="mt-1.5 h-11 rounded-xl border-cn-border bg-white"
              data-testid="counter-rate-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Explain your counter..."
              className="mt-1.5 h-11 rounded-xl border-cn-border bg-white"
              data-testid="counter-notes-input"
            />
          </div>
          <div className="rounded-2xl bg-cn-bone-100 p-3 text-sm">
            New total: <span className="font-display font-bold text-cn-primary">${(Number(rate) * offer.duration_hours).toFixed(2)}</span>{' '}
            for {offer.duration_hours}h
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="cn-btn cn-btn-ghost flex-1" data-testid="counter-cancel">Cancel</button>
            <button
              onClick={() => onSubmit(rate, notes)}
              className="cn-btn cn-btn-primary flex-1"
              data-testid="counter-submit"
            >
              Send counter <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ booking, onClose, onSubmit }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">How was the visit?</DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Rating</Label>
            <div className="mt-2 flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className={`w-10 h-10 rounded-full border transition-colors ${
                    n <= rating ? 'bg-cn-terracotta-500 border-cn-terracotta-500 text-white' : 'bg-white border-cn-border text-cn-text-muted hover:bg-cn-bone-100'
                  }`}
                  data-testid={`review-star-${n}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Comment (optional)</Label>
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience..."
              className="mt-1.5 h-11 rounded-xl border-cn-border bg-white"
              data-testid="review-comment-input"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="cn-btn cn-btn-ghost flex-1" data-testid="review-cancel">Cancel</button>
            <button
              onClick={() => onSubmit(rating, comment)}
              className="cn-btn cn-btn-primary flex-1"
              data-testid="review-submit"
            >
              Submit review
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
